import { FC } from 'react';
import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { useTranslation } from 'react-i18next';
import { Divider, Stack } from '@mui/material';
import { APP, ROUTES } from 'config';
import { sortBy } from 'lodash';
import {
  AdminSuppliersFilter,
  DateRangeFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { formatCountry, formatList } from 'utils/format';
import { shopItemCategories } from 'types/MarketplaceItems';
import { DownloadXlsAction } from 'components/actions';
import { useSearchParams } from 'utils/navigation';
import {
  COLOR_BY_STATUS,
  adminShopItemsFiltersSchema,
  useAdminShopItems,
  useAdminShopItemsInfo,
  useExportShopItemsTemplate,
  useExportToXls,
} from 'store/admin-shop';
import {
  shopItemStatuses,
  shopItemTypes,
  SHOP_ITEM_TYPE_LABELS,
} from 'store/shop';
import { useModalContext } from 'store/modal';
import { CreateShopItemModal } from 'containers/admin';
import { ShopItemsImportModal } from './atoms';

export const AdminShopItemsList: FC = () => {
  const { t, i18n } = useTranslation();
  const { openModal } = useModalContext();

  const { searchParams: filters, navigate } = useSearchParams(
    adminShopItemsFiltersSchema,
  );

  const { data: shopItems, isLoading } = useAdminShopItems(filters);
  const { data: info } = useAdminShopItemsInfo();

  const { mutate: exportToXls, isLoading: isExportingToXls } = useExportToXls();
  const {
    mutate: exportShopItemsTemplate,
    isLoading: isExportingShopItemsTemplate,
  } = useExportShopItemsTemplate();

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={1}
          >
            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('marketplaceItems.addMarketplaceItem'),
                  color: 'primary',
                  onClick: () => {
                    openModal({ component: CreateShopItemModal });
                  },
                },
                {
                  label: t('global.actions.bulk_update'),
                  onClick: () => {
                    openModal({ component: ShopItemsImportModal });
                  },
                },
                {
                  label: t('global.actions.export_template'),
                  onClick: () => {
                    exportShopItemsTemplate();
                  },
                  disabled: isExportingShopItemsTemplate,
                },
              ]}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={1}
          >
            <ListTotal total={shopItems?.total} />
            <Stack direction="row" alignItems="center" gap={1}>
              <Filters
                value={filters}
                initialValues={adminShopItemsFiltersSchema.parse({})}
                onChange={(v) => navigate({ params: { ...v, page: 1 } })}
                keysToExtractFromTotalCalculation={['search']}
              >
                {({ value: values, initialValues, setFilter }) => (
                  <>
                    <FilterButton
                      label={t('marketplaceOrders.orderDate')}
                      value={values.createdAt}
                      initialValue={initialValues.createdAt}
                      onChange={(v) => setFilter('createdAt', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <DateRangeFilter
                          value={value}
                          onApply={applyFilter}
                          onClear={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('marketplaceItems.supplier')}
                      value={values.suppliers}
                      initialValue={initialValues.suppliers}
                      onChange={(v) => setFilter('suppliers', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <AdminSuppliersFilter
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('marketplaceItems.countries')}
                      value={values.countries}
                      initialValue={initialValues.countries}
                      onChange={(v) => setFilter('countries', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="countries"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={sortBy(
                            info?.countries.map((alpha3) => ({
                              key: alpha3,
                              label: formatCountry(i18n.language, alpha3),
                            })) ?? [],
                            ['label'],
                          )}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('common.city.label')}
                      value={values.cities}
                      initialValue={initialValues.cities}
                      onChange={(v) => setFilter('cities', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="cities"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={APP.cities.map((city) => ({
                            key: city,
                            label: city,
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('benefits.benefit_type')}
                      value={values.categories}
                      initialValue={initialValues.categories}
                      onChange={(v) => setFilter('categories', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="categories"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          options={shopItemCategories.map((category) => ({
                            key: category,
                            label: t(
                              `marketplaceItems.itemCategory-${category}`,
                            ),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('integrations.type')}
                      value={values.type}
                      initialValue={initialValues.type}
                      onChange={(v) => setFilter('type', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="type"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          searchable={false}
                          options={shopItemTypes.map((type) => ({
                            key: type,
                            label: t(`marketplaceItems.itemType.${type}`, {
                              defaultValue: SHOP_ITEM_TYPE_LABELS[type],
                            }),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <FilterButton
                      label={t('common.status')}
                      value={values.status}
                      initialValue={initialValues.status}
                      onChange={(v) => setFilter('status', v)}
                    >
                      {({ value, applyFilter, clearFilter, close }) => (
                        <SelectFilter
                          name="status"
                          value={{ value }}
                          onApplyFilter={({ value }) => applyFilter(value)}
                          onClearFilter={clearFilter}
                          onCancel={close}
                          multiple
                          searchable={false}
                          options={shopItemStatuses.map((status) => ({
                            key: status,
                            label: t(`status.${status}`),
                          }))}
                        />
                      )}
                    </FilterButton>

                    <OpenSearchFilter
                      value={filters.search}
                      onApplyFilter={(v) => setFilter('search', v)}
                      placeholder={t('common.search')}
                    />
                  </>
                )}
              </Filters>

              <Divider orientation="vertical" variant="middle" flexItem />

              <DownloadXlsAction
                listLength={shopItems?.total ?? 0}
                exportToExcel={() => exportToXls({ filters })}
                loading={isExportingToXls}
              />
            </Stack>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={shopItems?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('common.name'),
            cell: ({ row: { original: shopItem } }) => (
              <Stack>
                <Link
                  label={shopItem.name}
                  to={ROUTES.admin.shopItems.details.settings.replace(
                    ':id',
                    shopItem.id,
                  )}
                />
                <Typography color="textSecondary">
                  {t('marketplace.employeeID', { id: shopItem.id })}
                </Typography>
              </Stack>
            ),
          },
          {
            id: 'type',
            header: t('integrations.type'),
            cell: ({ row: { original: shopItem } }) =>
              t(`marketplaceItems.itemType.${shopItem.type}`, {
                defaultValue: SHOP_ITEM_TYPE_LABELS[shopItem.type],
              }),
          },
          {
            id: 'supplier',
            header: t('marketplaceItems.supplier'),
            cell: ({ row: { original: shopItem } }) => (
              <Link
                label={shopItem.supplier.name}
                to={ROUTES.admin.suppliers.details.replace(
                  ':id',
                  shopItem.supplier.id,
                )}
              />
            ),
          },
          {
            id: 'country',
            header: t('marketplaceItems.countries'),
            cell: ({ row: { original: shopItem } }) =>
              formatList(
                i18n.language,
                shopItem.countries.map((country) =>
                  formatCountry(i18n.language, country),
                ),
              ),
          },
          {
            id: 'city',
            header: t('common.city.label'),
            cell: ({ row: { original: shopItem } }) =>
              formatList(i18n.language, shopItem.cities),
          },
          {
            id: 'category',
            header: t('marketplace.filtersItemCategory'),
            cell: ({ row: { original: shopItem } }) =>
              formatList(
                i18n.language,
                shopItem.categories.map((category) =>
                  t(`marketplaceItems.itemCategory-${category}`),
                ),
              ),
          },
          {
            id: 'sub_ordercount',
            header: t('menu.orders'),
            cell: ({ row: { original: shopItem } }) => shopItem.ordersCount,
            meta: { sort: true, align: 'right' },
          },

          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: shopItem } }) => (
              <Typography color={COLOR_BY_STATUS[shopItem.status]}>
                {t(`status.${shopItem.status}`)}
              </Typography>
            ),
          },
        ]}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          shopItems && {
            count: shopItems.total,
            page: filters.page,
            pageSize: filters.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
